import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, FileText, MessageSquare, Layers, ArrowRight, Home, Clock, FolderTree, LayoutGrid, Loader2 } from 'lucide-react';
import { api } from '../services/apiClient';
import { useFocusTrap } from '../lib/useFocusTrap';

// BP 36 — ⌘K command palette. Global, mounted once in AppShell. Opens on
// ⌘K / Ctrl+K, offers static navigation commands, and cross-snip search
// (guides / step narration / threads) via GET /api/v1/search. Keyboard-driven:
// ↑/↓ move, Enter activates, Esc closes. Results flatten into one selectable
// list so a single highlighted index drives navigation.

type Cmd =
  | { kind: 'nav'; id: string; label: string; sub?: string; icon: any; to: string }
  | { kind: 'guide'; id: string; label: string; sub?: string; to: string }
  | { kind: 'step'; id: string; label: string; sub?: string; to: string }
  | { kind: 'thread'; id: string; label: string; sub?: string; to: string };

export function CommandPalette() {
  const { t } = useTranslation(['console', 'nav']);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<{ guides: any[]; steps: any[]; threads: any[] }>({ guides: [], steps: [], threads: [] });
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const reqSeq = useRef(0);

  // A11Y-1 — focus trap owns Escape-to-close + initial focus while open;
  // called unconditionally (rules-of-hooks) even though the palette itself
  // early-returns null below when closed — `active: open` gates it internally.
  useFocusTrap(dialogRef, { active: open, onClose: () => setOpen(false), initialFocusRef: inputRef });

  const NAV: Cmd[] = useMemo(() => [
    { kind: 'nav', id: 'nav-console', label: t('nav:items.console'), sub: t('commandPalette.navSubs.home'), icon: Home, to: '/' },
    { kind: 'nav', id: 'nav-projects', label: t('nav:items.projects'), sub: t('commandPalette.navSubs.projects'), icon: FolderTree, to: '/projects' },
    { kind: 'nav', id: 'nav-board', label: t('nav:items.snipBoard'), sub: t('commandPalette.navSubs.board'), icon: LayoutGrid, to: '/board' },
    { kind: 'nav', id: 'nav-logbook', label: t('nav:items.logbook'), sub: t('commandPalette.navSubs.logbook'), icon: Clock, to: '/logbook' },
    { kind: 'nav', id: 'nav-shared', label: t('nav:items.sharedWithMe'), sub: t('commandPalette.navSubs.shared'), icon: Layers, to: '/shared' },
  ], [t]);

  // ── Open / close on ⌘K · Ctrl+K — Esc-to-close is handled by useFocusTrap ──
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(o => !o);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Reset query/results when opening — initial focus is handled by useFocusTrap.
  useEffect(() => {
    if (open) {
      setQ(''); setResults({ guides: [], steps: [], threads: [] }); setActive(0);
    }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setResults({ guides: [], steps: [], threads: [] }); setLoading(false); return; }
    setLoading(true);
    const seq = ++reqSeq.current;
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/api/v1/search', { params: { q: term } });
        if (seq === reqSeq.current) { setResults({ guides: r.data.guides || [], steps: r.data.steps || [], threads: r.data.threads || [] }); setActive(0); }
      } catch { if (seq === reqSeq.current) setResults({ guides: [], steps: [], threads: [] }); }
      finally { if (seq === reqSeq.current) setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  // Flatten into one selectable list (nav commands filtered by the query).
  const items = useMemo<Cmd[]>(() => {
    const term = q.trim().toLowerCase();
    const nav = term
      ? NAV.filter(n => n.label.toLowerCase().includes(term) || (n.sub || '').toLowerCase().includes(term))
      : NAV;
    const guides: Cmd[] = results.guides.map(g => ({ kind: 'guide', id: 'g-' + g.id, label: g.title || t('commandPalette.untitledGuide'), sub: g.public_id || g.intent_summary, to: `/guides/${g.id}` }));
    const steps: Cmd[] = results.steps.map((s, i) => ({ kind: 'step', id: `s-${s.guide_id}-${s.step_number}-${i}`, label: s.snippet, sub: `${s.guide_title} · step ${s.step_number}`, to: `/guides/${s.guide_id}` }));
    const threads: Cmd[] = results.threads.map(th => ({ kind: 'thread', id: 't-' + th.id, label: th.title || t('commandPalette.untitledThread'), sub: th.guide_title, to: `/guides/${th.guide_id}` }));
    return [...nav, ...guides, ...steps, ...threads];
  }, [q, results, NAV, t]);

  useEffect(() => { if (active >= items.length) setActive(0); }, [items.length, active]);

  const go = useCallback((cmd?: Cmd) => {
    const target = cmd || items[active];
    if (!target) return;
    setOpen(false);
    navigate(target.to);
  }, [items, active, navigate]);

  // List keyboard nav.
  function onListKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(); }
  }

  if (!open) return null;

  const iconFor = (c: Cmd) =>
    c.kind === 'nav' ? c.icon : c.kind === 'guide' ? FileText : c.kind === 'step' ? FileText : MessageSquare;

  // Section boundaries for subtle group labels.
  let lastKind = '';
  const sectionHeaders: Record<string, string> = {
    nav: t('commandPalette.sectionHeaders.nav'),
    guide: t('commandPalette.sectionHeaders.guide'),
    step: t('commandPalette.sectionHeaders.step'),
    thread: t('commandPalette.sectionHeaders.thread'),
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[12vh] px-4"
      style={{ background: 'var(--ws-overlay)' }}
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      role="dialog"
      aria-modal="true"
      aria-label={t('commandPalette.ariaLabel')}
    >
      <div ref={dialogRef} className="w-full max-w-xl bg-ws-tile border border-ws-border rounded-xl shadow-2xl overflow-hidden" onKeyDown={onListKey}>
        <div className="flex items-center gap-2 px-4 border-b border-ws-border">
          <Search size={16} className="text-ws-ink-3 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('commandPalette.searchPlaceholder')}
            className="flex-1 bg-transparent outline-none focus-visible:ring-2 focus-visible:ring-ws-accent rounded-sm py-3.5 text-sm text-ws-ink placeholder:text-ws-ink-4"
          />
          {loading && <Loader2 size={15} className="animate-spin text-ws-ink-3" />}
          <kbd className="text-[10px] text-ws-ink-4 border border-ws-border rounded px-1.5 py-0.5">{t('commandPalette.escKey')}</kbd>
        </div>

        <div className="max-h-[55vh] overflow-y-auto py-1">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-ws-ink-4">
              {q.trim().length < 2 ? t('commandPalette.hintTypeToSearch') : t('commandPalette.noMatches')}
            </div>
          )}
          {items.map((c, i) => {
            const Icon = iconFor(c);
            const showHeader = c.kind !== lastKind;
            lastKind = c.kind;
            const header = showHeader ? sectionHeaders[c.kind] : null;
            return (
              <div key={c.id}>
                {header && <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wide text-ws-ink-4 font-semibold">{header}</div>}
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(c)}
                  className={`w-full text-left px-4 py-2 flex items-center gap-3 ${i === active ? 'bg-ws-accent-quiet' : 'hover:bg-ws-surface-2'}`}
                >
                  <Icon size={15} className="text-ws-ink-3 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-ws-ink truncate">{c.label}</span>
                    {c.sub && <span className="block text-xs text-ws-ink-4 truncate">{c.sub}</span>}
                  </span>
                  {i === active && <ArrowRight size={14} className="text-ws-accent shrink-0" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
